import moment from 'moment';
import { useEffect, useState } from 'react';
import {
    Collapse,
    Divider,
    Drawer,
    PageHeader,
    Space,
    Spin,
    Tag,
    Timeline,
    Typography,
} from 'antd';
import { BookingOrderStatus } from '../../types/booking';
import { getOrderRequest, getOrderResponse, getOrderWebhooks } from '../../api/booking';
import { getStatusChipClass } from '../../utils/bookings';
import { STATUSES_TEXT } from '../../const/booking';

interface Props {
    order: BookingOrderStatus;
    open: boolean;
    onClose: () => void;
}

const { Title, Text } = Typography;

const OrderDetail = ({ order, open, onClose }: Props) => {
    const [orderId, setOrderId] = useState<string | null>(order._id);
    const [request, setRequest] = useState<{} | null>(null);
    const [response, setResponse] = useState<{} | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [webhooks, setWebhooks] = useState<any[]>([]);

    const fetchOrderRequest = () => {
        setLoading(true);
        getOrderRequest(orderId as string)
            .then(({ data }) => {
                setRequest(data.data);
            })
            .catch((error) => console.log(error))
            .finally(() => setLoading(false));
    };

    const fetchOrderResponse = () => {
        setLoading(true);
        getOrderResponse(orderId as string)
            .then(({ data }) => {
                setResponse(data.data);
            })
            .catch((error) => console.log(error))
            .finally(() => setLoading(false));
    };

    const fetchOrderWebhooks = () => {
        setLoading(true);
        getOrderWebhooks(orderId as string)
            .then(({ data }) => {
                setWebhooks(data.data);
            })
            .catch((error) => console.log(error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!orderId) return;
        fetchOrderRequest();
        fetchOrderResponse();
        fetchOrderWebhooks();
    }, [orderId]);

    return (
        <Drawer
            title="Order Details"
            width={750}
            onClose={onClose}
            open={open}
        >
            <PageHeader
                title={order.providerName}
                subTitle={(
                    <Tag {...getStatusChipClass(order.status)}>{order.status}</Tag>
                )}
                style={{ padding: 0 }}
                footer={(
                    <Text style={{ fontSize: 12, fontStyle: 'italic' }}>
                        {`created at: ${moment(order.createdAt).format('DD MMM YYYY - HH:mm:ss')}`}
                    </Text>
                )}
            />
            <Divider />
            <Collapse
                bordered={false}
                ghost
                defaultActiveKey={['3']}
            >
                <Collapse.Panel
                    header={(
                        <Title level={5} style={{ marginBottom: 0 }}>
                            Request
                        </Title>
                    )}
                    key="1"
                >
                    {loading ? (
                        <Space>
                            <Spin size="small" />
                            <Text>Loading...</Text>
                        </Space>
                    ) : request ? (
                        <pre>
                            {JSON.stringify(request, null, 2)}
                        </pre>
                    ) : (
                        <Text>
                            No request found
                        </Text>
                    )}
                </Collapse.Panel>
                <Collapse.Panel
                    header={(
                        <Title level={5} style={{ marginBottom: 0 }}>
                            Response
                        </Title>
                    )}
                    key="2"
                >
                    {loading ? (
                        <Space>
                            <Spin size="small" />
                            <Text>Loading...</Text>
                        </Space>
                    ) : response ? (
                        <pre>
                            {JSON.stringify(response, null, 2)}
                        </pre>
                    ) : (
                        <Text>
                            No response found
                        </Text>
                    )}
                </Collapse.Panel>
                <Collapse.Panel
                    header={(
                        <Title level={5} style={{ marginBottom: 0 }}>
                            Webhooks - ACCEPTED
                        </Title>
                    )}
                    key="3"
                >
                    {loading ? (
                        <Space>
                            <Spin size="small" />
                            <Text>Loading...</Text>
                        </Space>
                    ) : webhooks ? (
                        <pre>
                            {JSON.stringify(webhooks, null, 2)}
                        </pre>
                    ) : (
                        <Text>
                            No webhooks found
                        </Text>
                    )}
                </Collapse.Panel>
                <Collapse.Panel
                    header={(
                        <Title level={5} style={{ marginBottom: 0 }}>
                            Status updates
                        </Title>
                    )}
                    key="4"
                >
                    <Timeline mode="left">
                        {
                            order.statusUpdates.map((statusUpdate) => {
                                const time = moment(statusUpdate.timeStamp).local().format('HH:mm:ss:SSS');
                                const date = moment(statusUpdate.timeStamp).local().format('ddd DD MMM');
                                return (
                                    <Timeline.Item
                                        key={statusUpdate._id}
                                        label={(
                                            <Space size={4}>
                                                <Text>
                                                    {date}
                                                </Text>
                                                <Text type="secondary">
                                                    at
                                                </Text>
                                                <Text>
                                                    {time}
                                                </Text>
                                            </Space>
                                        )}
                                    >
                                        <Space direction="vertical" size={8}>
                                            <Text style={{ fontSize: 12, lineHeight: 2.5 }}>
                                                Change from
                                                {' '}
                                                <Tag color="blue">
                                                    {statusUpdate.previousStatus}
                                                </Tag>
                                                to
                                                {' '}
                                                <Tag color="green">
                                                    {statusUpdate.newStatus}
                                                </Tag>
                                            </Text>
                                            {
                                                statusUpdate.newStatus === STATUSES_TEXT.ACCEPTED && (
                                                    <Text style={{ fontSize: 12, lineHeight: 1.5 }}>
                                                        <Space direction="vertical" size={0}>
                                                            <Text>
                                                                {`Assigned to: ${statusUpdate.driver?.name} ${statusUpdate.driver?.lastName || ''}`}
                                                            </Text>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                {statusUpdate.driver?.email || 'No email found'}
                                                            </Text>
                                                        </Space>
                                                    </Text>
                                                )
                                            }
                                        </Space>
                                    </Timeline.Item>
                                );
                            })
                        }
                    </Timeline>
                </Collapse.Panel>
            </Collapse>
        </Drawer>
    );
};

export default OrderDetail;
